import React, { useCallback, useEffect } from 'react';
import Events from '../../../events';
import { EventsType } from '../../../events-types';
import { getObjectsByRegEx, save, deleteDoc, MARKET_CBD, getObjectByDoc } from '../../../api/Compounds';
import { iBaseCardWithComponentProps, iBaseInnerCardProps } from "../../types";
import { CardWithBaseComponent } from "../../common/BaseCard";
import { renderFields } from "../../../utils/pair_entry";
import FormEditModal from "../../common/FormEditModal";
import AuditModal from "../../common/AuditModal";
import DisplayDeleteModal from "../../common/DeleteConfirmationModal";
import { isNotEmpty } from "../../../utils/string";
import { useRoles } from "../../../App.Context";
import { Market_CBD } from "./types";

const _MarketCBDCard: React.FC<iBaseInnerCardProps> = ({ setIsShow, setError, setData, data, setTitle, searchBy, searchByDoc, isActive }) => {
    const UUID = '_MarketCBDCard';
    const roles = useRoles();
    const hasReadOnlyRole = roles.includes("ReadOnly");

    // MODAL EDIT CALLBACKS
    const saveData = useCallback(async (data: any) => {
        save(MARKET_CBD, data)
            .then((res) => {
                setData(res);
                setIsShow(true);
            })
            .catch(setError);
    }, [setData, setIsShow, setError]);

    const deleteData = useCallback(async (data: any) => {
        deleteDoc(MARKET_CBD, data)
            .then((res) => {
                setData({});
                setIsShow(false);
            })
            .catch(setError);
    }, [setData, setIsShow, setError]);

    const undo = useCallback(async (undoData: any) => {
        Object.assign(data, undoData);
        save(MARKET_CBD, data)
            .then((res) => {
                setData(res);
                setIsShow(true);
            })
            .catch(setError);
    }, [data, setData, setIsShow, setError]);

    // EVENT HANDLERS
    const createEvent = useCallback((event: { detail: any }) => {
        if (isNotEmpty(event.detail)) {
            delete event.detail._id;
            saveData(event.detail);
        }
    }, [saveData]);

    const searchEvent = useCallback((event: { detail: string }) => {
        searchBy?.(getObjectsByRegEx, MARKET_CBD, 'DRUG_NAME,ACTIVE_INGREDIENT,THERAPEUTICAL_INDICATION', event.detail);
    }, [searchBy]);

    const searchByCBDDrugEvent = useCallback((event: { detail: any }) => {
        setTitle(event.detail.DRUG_NAME);
        setData(event.detail);
        setIsShow(true);
    }, [setTitle, setData, setIsShow]);

    const searchByCBDMarketDrugEvent = useCallback((event: { detail: any }) => {
        searchByDoc?.(getObjectByDoc, MARKET_CBD, event.detail, false, 'DRUG_NAME');
    }, [searchByDoc]);

    // EFFECTS
    useEffect(() => {
        Events.on(EventsType.CREATE, createEvent, UUID);
        if (isActive) {
            Events.on(EventsType.SEARCH, searchEvent, UUID);
            Events.on(EventsType.SEARCH_BY_CBD_DRUG, searchByCBDDrugEvent, UUID);
            Events.on(EventsType.SEARCH_BY_CBD_MARKET_DRUG, searchByCBDMarketDrugEvent, UUID);
        }
        return () => {
            Events.off(EventsType.CREATE, UUID);
            Events.off(EventsType.SEARCH, UUID);
            Events.off(EventsType.SEARCH_BY_CBD_DRUG, UUID);
            Events.off(EventsType.SEARCH_BY_CBD_MARKET_DRUG, UUID);
        };
    }, [isActive, createEvent, searchEvent, searchByCBDDrugEvent, searchByCBDMarketDrugEvent]); // Fixed typo: replaced duplicate searchByCBDMarketDrugEvent with searchByCBDDrugEvent

    // RENDERS
    return (
        <p>
            {renderFields(Market_CBD, data)}
            {!hasReadOnlyRole && (
                <>
                    <FormEditModal
                        save={saveData}
                        data={data}
                        schema={Market_CBD}
                    />
                    <DisplayDeleteModal
                        deleteDoc={deleteData}
                        data={data}
                        schema={Market_CBD}
                    />
                </>
            )}
            <AuditModal id={data._id} undo={undo} />
        </p>
    );
};

const MarketCBDCard: React.FC<Omit<iBaseCardWithComponentProps, 'cardComponent'>> = ({ header, width, eventGroupToTrigger }) => {
    return <CardWithBaseComponent cardComponent={_MarketCBDCard} header={header} width={width} eventGroupToTrigger={eventGroupToTrigger} />;
};

export default React.memo(MarketCBDCard);
